import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryDataSource } from '../../api/query';
import { sourceIdBul } from '../../utils/raporSonek';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';

interface KategoriSatiri {
  kategori: string;
  ciro: number;
  miktar: number;
  kar: number;
  pay_yuzde: number;
}

interface UrunKarSatiri {
  stok_kodu: string;
  urun_adi: string;
  kategori: string;
  ciro: number;
  maliyet: number;
  kar: number;
  kar_marji: number;
}

interface CokSatanSatiri {
  stok_kodu: string;
  urun_adi: string;
  barkod: string;
  miktar: number;
  ciro: number;
  kar: number;
  kar_marji: number;
}

interface AzSatanSatiri {
  stok_kodu: string;
  urun_adi: string;
  kategori: string;
  miktar: number;
  ciro: number;
  son_satis_tarihi: string;
}

interface SubeKategoriSatiri {
  magaza: string;
  kategori: string;
  ciro: number;
  miktar: number;
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
}

/**
 * Yuzde bicimlendirici: `primitives.fmtPct` BURADA UYGUN DEGIL - o negatifte
 * U+2212 ("−") kullanir, marj kolonunun beklenen bicimi ise ASCII tire
 * ("-"). `toFixed` negatif sayida zaten ASCII tire uretir, o yuzden ayrica
 * isaret eklenmiyor (fmtPct'nin aksine).
 */
function fmtYuzde1(n: number): string {
  return `${n.toFixed(1).replace('.', ',')}%`;
}

/** Marj = kar / ciro * 100. Ciro 0 ise SIFIRA BOLME yapilmaz, null doner. */
function marjHesapla(kar: number, ciro: number): number | null {
  if (!ciro) return null;
  return (kar / ciro) * 100;
}

type SiralamaAlani = 'ciro' | 'pay' | 'kar' | 'marj';

interface SiralamaDurumu {
  alan: SiralamaAlani;
  yon: 'asc' | 'desc';
}

/**
 * Siralama kolonunun HAM sayisal degeri. `marj` icin ciro=0 (tanimsiz marj)
 * -Infinity'e esitlenir - "yok sayilir/kirpilir" DEGIL, siralamada her iki
 * yonde de dogal olarak en dibe duser (ozel bir durum kolu YOK).
 */
function alanDegeri(k: KategoriSatiri, alan: SiralamaAlani): number {
  switch (alan) {
    case 'ciro':
      return Number(k.ciro);
    case 'pay':
      return Number(k.pay_yuzde);
    case 'kar':
      return Number(k.kar);
    case 'marj': {
      const m = marjHesapla(Number(k.kar), Number(k.ciro));
      return m === null ? -Infinity : m;
    }
  }
}

interface SiralanabilirBaslikProps {
  label: string;
  alan: SiralamaAlani;
  siralama: SiralamaDurumu;
  onTikla: (alan: SiralamaAlani) => void;
}

/** Siralanabilir kolon basligi: aktif kolon+yon oku (▲/▼) ve punto/renk
 * agirligiyla belirtilir - YENI RENK yok, mevcut ink tonlari kullanilir. */
function SiralanabilirBaslik({ label, alan, siralama, onTikla }: SiralanabilirBaslikProps) {
  const aktif = siralama.alan === alan;
  const ariaSort = aktif ? (siralama.yon === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th aria-sort={ariaSort} className="text-right font-medium py-2 px-3">
      <button
        type="button"
        data-testid={`kategori-baslik-${alan}`}
        onClick={() => onTikla(alan)}
        className={`inline-flex items-center gap-1 focus-ring ${
          aktif ? 'text-ink-900 font-semibold' : 'text-ink-500'
        }`}
      >
        {label}
        {aktif && <span aria-hidden="true">{siralama.yon === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

/**
 * Teshis katmani: kategori satirina tiklayinca urun kirilimi AYNI EKRANDA
 * (bu bilesenin icinde, ayni tabloda) acilir - ayri bir rapora/sayfaya
 * atlatma YOK.
 *
 * 016/014/018/017 SADECE `@BASTARIH`/`@BITTARIH` alir - kategori parametresi
 * YOK (sozluk degisikligi 9 firmayi etkiler, bu ekran icin kabul edilmedi).
 * Bu yuzden suzme ISTEMCIDE yapilir: her dort kaynak da bir kez (kategori
 * secilir secilmez, lazy) cekilir ve query key secilen kategoriyi ICERMEZ -
 * baska bir kategoriye tiklamak YENI SORGU ACMAZ, yalniz mevcut veri
 * yeniden suzulur.
 *
 * 014 (En Cok Satan Urunler) kategori kolonu HIC TASIMIYOR. Uydurma
 * kategori atamak yerine 016'nin `stok_kodu -> kategori` haritasina JOIN
 * edilir; 016'da karsiligi olmayan bir stok_kodu o kategoriye ASLA
 * atfedilmez (sessizce disarida kalir).
 *
 * 017 (Şube × Kategori Satış) secilen kategorinin subeler arasi dagilimini
 * verir - "bu kategori HANGI subede satiliyor" sorusunun cevabi. 017
 * cozulmezse (rapor yok/ad tutmuyor) yalniz bu alt bolum gizlenir, urun
 * kirilimimin geri kalani (016/014/018) etkilenmez. Tek sube varsa da
 * (dagilim yok, tek deger karsilastirilamaz) alt bolum gizlenir.
 *
 * Urun kirilimindaki DORT alt bolum (016 urun karliligi / 017 sube
 * dagilimi / 014 en cok satan / 018 az-hic satan) BIRBIRINDEN BAGIMSIZ
 * gorunur: 016'nin secilen kategori icin BOS donmesi (ör. o kategoride
 * karlilik verisi yoksa) 014/017/018'in dolu alt bolumlerini GIZLEMEZ.
 * Kirilim container'i yalniz DORDU DE bos donerse tumden gizlenir.
 */
export function KategoriBlok({ reports, donem }: Props) {
  const [secilenKategori, setSecilenKategori] = useState<string | null>(null);
  const [siralama, setSiralama] = useState<SiralamaDurumu>({ alan: 'ciro', yon: 'desc' });
  const params = { '@BASTARIH': donem.bas, '@BITTARIH': donem.bit };

  // CONTROLLER RULING F4: sonek + ad kontrolu - ad tutmazsa blok/alt bolum
  // sessizce gizlenir, yanlis firmanin verisini basmaz.
  const idKategori = sourceIdBul(reports, 15, 'Kategori Bazında Satış');
  const idUrunKar = sourceIdBul(reports, 16, 'Ürün Kârlılığı');
  const idCokSatan = sourceIdBul(reports, 14, 'En Çok Satan Ürünler');
  const idAzSatan = sourceIdBul(reports, 18, 'Az/Hiç Satan Ürünler');
  const idSube = sourceIdBul(reports, 17, 'Şube × Kategori Satış');

  const kategoriQ = useQuery({
    queryKey: ['sa-kategori', idKategori, params],
    queryFn: () => queryDataSource<KategoriSatiri>({ sourceId: idKategori!, parameters: params }),
    enabled: !!idKategori,
    throwOnError: false,
  });

  // Lazy: kategori secilmeden bu dort sorgu hic acilmaz. Query key
  // `secilenKategori`'yi ICERMEZ - "bir kez cekilir" budur.
  const urunKarQ = useQuery({
    queryKey: ['sa-kategori-urun', idUrunKar, params],
    queryFn: () => queryDataSource<UrunKarSatiri>({ sourceId: idUrunKar!, parameters: params }),
    enabled: !!idUrunKar && secilenKategori !== null,
    throwOnError: false,
  });

  const cokSatanQ = useQuery({
    queryKey: ['sa-kategori-coksatan', idCokSatan, params],
    queryFn: () => queryDataSource<CokSatanSatiri>({ sourceId: idCokSatan!, parameters: params }),
    enabled: !!idCokSatan && secilenKategori !== null,
    throwOnError: false,
  });

  const azSatanQ = useQuery({
    queryKey: ['sa-kategori-azsatan', idAzSatan, params],
    queryFn: () => queryDataSource<AzSatanSatiri>({ sourceId: idAzSatan!, parameters: params }),
    enabled: !!idAzSatan && secilenKategori !== null,
    throwOnError: false,
  });

  const subeQ = useQuery({
    queryKey: ['sa-kategori-sube', idSube, params],
    queryFn: () => queryDataSource<SubeKategoriSatiri>({ sourceId: idSube!, parameters: params }),
    enabled: !!idSube && secilenKategori !== null,
    throwOnError: false,
  });

  const kategoriler = useMemo(() => {
    const rows = kategoriQ.data?.data ?? [];
    const carpan = siralama.yon === 'asc' ? 1 : -1;
    return [...rows].sort(
      (a, b) => (alanDegeri(a, siralama.alan) - alanDegeri(b, siralama.alan)) * carpan,
    );
  }, [kategoriQ.data, siralama]);

  // stok_kodu -> kategori: 014'un JOIN kaynagi. 016 yoksa/bos ise bos harita
  // doner - 014 o durumda hicbir satira kategori ATFEDEMEZ (uydurma yok).
  const stokKategoriHaritasi = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of urunKarQ.data?.data ?? []) {
      map.set(String(r.stok_kodu), String(r.kategori));
    }
    return map;
  }, [urunKarQ.data]);

  const urunlerFiltrelenmis = useMemo(() => {
    if (secilenKategori === null) return [];
    return (urunKarQ.data?.data ?? []).filter((r) => r.kategori === secilenKategori);
  }, [urunKarQ.data, secilenKategori]);

  const cokSatanlarFiltrelenmis = useMemo(() => {
    if (secilenKategori === null) return [];
    return (cokSatanQ.data?.data ?? []).filter(
      (r) => stokKategoriHaritasi.get(String(r.stok_kodu)) === secilenKategori,
    );
  }, [cokSatanQ.data, stokKategoriHaritasi, secilenKategori]);

  const azSatanlarFiltrelenmis = useMemo(() => {
    if (secilenKategori === null) return [];
    return (azSatanQ.data?.data ?? []).filter((r) => r.kategori === secilenKategori);
  }, [azSatanQ.data, secilenKategori]);

  // Magaza bazinda TOPLANIR (gunler ustunden) - tek magaza -> dagilim yok,
  // karsilastirilacak ikinci bir deger olmadigi icin alt bolum gizlenir.
  const subeDagilimiFiltrelenmis = useMemo(() => {
    if (secilenKategori === null) return [];
    const map = new Map<string, { magaza: string; ciro: number; miktar: number }>();
    for (const r of subeQ.data?.data ?? []) {
      if (r.kategori !== secilenKategori) continue;
      const magaza = String(r.magaza);
      if (!map.has(magaza)) map.set(magaza, { magaza, ciro: 0, miktar: 0 });
      const v = map.get(magaza)!;
      v.ciro += Number(r.ciro ?? 0);
      v.miktar += Number(r.miktar ?? 0);
    }
    return Array.from(map.values()).sort((a, b) => b.ciro - a.ciro);
  }, [subeQ.data, secilenKategori]);

  /** Siralama basligina tiklama: ayni kolon -> yon TERSINE doner; farkli
   * kolon -> o kolona ARTAN (asc) siralamayla gecilir. */
  function setSiralamaAlani(alan: SiralamaAlani) {
    setSiralama((cur) =>
      cur.alan === alan ? { alan, yon: cur.yon === 'asc' ? 'desc' : 'asc' } : { alan, yon: 'asc' },
    );
  }

  if (!idKategori) return null; // rapor yok/ad tutmuyor -> sorgu bile acilmaz
  if (kategoriler.length === 0) return null; // bos durum kurali: veri yok/henuz gelmedi -> blok gizlenir

  // Her alt bolum KENDI verisine gore gorunur - 016'nin secilen kategori
  // icin BOS donmesi 014/017/018'in dolu olabilecegi alt bolumleri
  // GIZLEMEMELI (ekranin genelindeki "bos durum kurali" burada da gecerli:
  // veri yoksa o PARCA gizlenir, komsu parcalar etkilenmez). Kaynaklardan
  // biri hic cozulmediyse (rapor yok/ad tutmuyor -> id yok) o kaynak
  // "hazir degil" sayilmaz - zaten hicbir zaman sorgulanmayacagi icin
  // sonsuza kadar beklemeyiz.
  const urunHazir = !idUrunKar || urunKarQ.data !== undefined;
  const cokSatanHazir = !idCokSatan || cokSatanQ.data !== undefined;
  const azSatanHazir = !idAzSatan || azSatanQ.data !== undefined;
  const subeHazir = !idSube || subeQ.data !== undefined;
  const tumKaynaklarHazir = urunHazir && cokSatanHazir && azSatanHazir && subeHazir;

  const herhangiBirIcerikVar =
    urunlerFiltrelenmis.length > 0 ||
    cokSatanlarFiltrelenmis.length > 0 ||
    azSatanlarFiltrelenmis.length > 0 ||
    subeDagilimiFiltrelenmis.length >= 2;

  // Urun kirilimi container'i, TUM kaynaklar (var olanlar) veri getirinceye
  // kadar gorunmez - boylece "acildi" iddiasi zaten cozulmus veriyle gelir
  // (F5: cagri degil cozum bekleme kuralina bu sekilde uyulur). Icerikte
  // HICBIR alt bolumun gosterecek bir seyi yoksa (dorddu de bos donduyse)
  // container'in kendisi de gizlenir - bos bir kutu ACILMAZ.
  const kirilimGorunur = secilenKategori !== null && tumKaynaklarHazir && herhangiBirIcerikVar;

  return (
    <div data-testid="blok-kategori">
      <SectionCard
        title="Kategori bazında satış"
        subtitle="Kategoriye tıklayınca ürün kırılımı aynı ekranda açılır"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3">Kategori</th>
                <SiralanabilirBaslik label="Ciro" alan="ciro" siralama={siralama} onTikla={setSiralamaAlani} />
                <SiralanabilirBaslik label="Pay" alan="pay" siralama={siralama} onTikla={setSiralamaAlani} />
                <SiralanabilirBaslik label="Kâr" alan="kar" siralama={siralama} onTikla={setSiralamaAlani} />
                <SiralanabilirBaslik label="Marj" alan="marj" siralama={siralama} onTikla={setSiralamaAlani} />
              </tr>
            </thead>
            <tbody>
              {kategoriler.map((k) => {
                const kategoriAdi = String(k.kategori);
                const acik = secilenKategori === kategoriAdi;
                const ciro = Number(k.ciro);
                const kar = Number(k.kar);
                const marj = marjHesapla(kar, ciro);
                return (
                  <Fragment key={kategoriAdi}>
                    <tr
                      data-testid={`kategori-satir-${kategoriAdi}`}
                      onClick={() =>
                        setSecilenKategori((cur) => (cur === kategoriAdi ? null : kategoriAdi))
                      }
                      className="cursor-pointer border-b border-border last:border-b-0 hover:bg-ink-50 focus-ring"
                    >
                      <td className="py-2 pr-3 text-ink-900 font-medium truncate">{kategoriAdi}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-900">{fmtTRY(ciro)}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-500">
                        {fmtYuzde1(Number(k.pay_yuzde))}
                      </td>
                      <td className={`py-2 px-3 text-right tnum ${kar < 0 ? 'text-neg-700' : 'text-ink-900'}`}>
                        {fmtTRY(kar)}
                      </td>
                      <td
                        className={`py-2 pl-3 text-right tnum ${
                          marj !== null && marj < 0 ? 'text-neg-700' : 'text-ink-500'
                        }`}
                      >
                        {marj !== null ? fmtYuzde1(marj) : '—'}
                      </td>
                    </tr>
                    {acik && kirilimGorunur && (
                      <tr>
                        <td colSpan={5} className="p-0">
                          <div data-testid="urun-kirilim" className="pl-4 pr-2 py-3 flex flex-col gap-4 bg-ink-50">
                            {urunlerFiltrelenmis.length > 0 && (
                              <div className="flex flex-col gap-1.5">
                                <h3 className="text-[12px] font-semibold text-ink-700">
                                  {kategoriAdi} · Ürün kârlılığı
                                </h3>
                                {urunlerFiltrelenmis.map((u) => (
                                  <div
                                    key={u.stok_kodu}
                                    className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
                                  >
                                    <span className="truncate">{u.urun_adi}</span>
                                    <span className="flex items-center gap-4 shrink-0 tnum">
                                      <span>{fmtTRY(Number(u.ciro))}</span>
                                      <span
                                        className={Number(u.kar_marji) < 0 ? 'text-neg-700' : 'text-ink-500'}
                                      >
                                        {fmtYuzde1(Number(u.kar_marji))}
                                      </span>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {subeDagilimiFiltrelenmis.length >= 2 && (
                              <div data-testid="kategori-sube-dagilimi" className="flex flex-col gap-1.5">
                                <h3 className="text-[12px] font-semibold text-ink-700">Şube dağılımı</h3>
                                {subeDagilimiFiltrelenmis.map((s) => (
                                  <div
                                    key={s.magaza}
                                    className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
                                  >
                                    <span className="truncate">{s.magaza}</span>
                                    <span className="tnum">{fmtTRY(s.ciro)}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {cokSatanlarFiltrelenmis.length > 0 && (
                              <div className="flex flex-col gap-1.5">
                                <h3 className="text-[12px] font-semibold text-ink-700">En çok satan</h3>
                                {cokSatanlarFiltrelenmis.map((u) => (
                                  <div
                                    key={u.stok_kodu}
                                    className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
                                  >
                                    <span className="truncate">{u.urun_adi}</span>
                                    <span className="tnum">{fmtTRY(Number(u.ciro))}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {azSatanlarFiltrelenmis.length > 0 && (
                              <div className="flex flex-col gap-1.5">
                                <h3 className="text-[12px] font-semibold text-ink-700">Az/hiç satan</h3>
                                {azSatanlarFiltrelenmis.map((u) => (
                                  <div
                                    key={u.stok_kodu}
                                    className="flex items-center justify-between gap-3 text-[12px] text-ink-700"
                                  >
                                    <span className="truncate">{u.urun_adi}</span>
                                    <span className="tnum text-ink-500">{u.son_satis_tarihi}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
